import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CheckCircle, Wallet, Building2, Download, Home, ArrowRight } from 'lucide-react';
import Footer from '../components/Footer';
import { useAgentAuth } from '../context/AgentAuthContext';
import { getUserFacingBookingId, isTripjackSupplierBookingId } from '../utils/userFacingBookingId';

const BookingSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshWallet, refreshCreditBalance } = useAgentAuth();

  const {
    bookingId,
    paymentMode,
    amountDebited,
    remainingBalance,
    bookingDetails,
    seatWarning,   // surfaced when supplier rejected seat SSR (errCode 1131
                   // fallback) — booking is complete but seat preference lost
    // Sprint May-2026 · Hold flow metadata. When `isHold === true`, the
    // page renders a clear "Fare on hold — pay before X" panel instead of
    // the wallet-debited Payment Summary.
    isHold,
    holdUntil,
    holdTimeLimit,
    holdAmount,
  } = location.state || {};

  const isPendingSupplierConfirmation =
    !isHold
    && (
      bookingDetails?.booking_status === 'PENDING_SUPPLIER_CONFIRMATION'
      || bookingDetails?.statusMeta?.label === 'Pending Supplier Confirmation'
    )
    && !bookingDetails?.pnr;

  useEffect(() => {
    // Refresh wallet or credit balance after successful booking
    if (paymentMode === 'CREDIT') {
      refreshCreditBalance();
    } else if (paymentMode === 'WALLET') {
      refreshWallet();
    }

    // Clear localStorage
    localStorage.removeItem('selectedFlight');
    localStorage.removeItem('holdBooking');
    localStorage.removeItem('pendingBooking');
  }, [paymentMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const displayBookingRef = getUserFacingBookingId({
    bookingId,
    booking_id: bookingDetails?.booking_id,
    id: bookingDetails?.id,
    ...bookingDetails,
  });
  const bookingRefShown =
    displayBookingRef !== '—'
      ? displayBookingRef
      : (bookingId && !isTripjackSupplierBookingId(bookingId) ? bookingId : '—');

  if (!bookingId && displayBookingRef === '—') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">No Booking Found</h2>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-[#659A9B] text-white rounded-lg hover:bg-[#335E77]"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-teal-50">
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Success Icon & Message */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-green-100 rounded-full mb-6">
            <CheckCircle className="w-16 h-16 text-green-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-800 mb-2">
            {isHold
              ? 'Fare on Hold'
              : (isPendingSupplierConfirmation ? 'Booking Submitted' : 'Booking Confirmed!')}
          </h1>
          <p className="text-lg text-gray-600">
            {isHold
              ? 'Seats are reserved. Pay & confirm before the hold expires.'
              : (isPendingSupplierConfirmation
                ? 'Supplier confirmation is pending. Your PNR/ticket will appear once the airline confirms.'
                : 'Your flight booking has been successfully completed')}
          </p>
        </div>

        {/* Pending supplier confirmation banner */}
        {isPendingSupplierConfirmation && (
          <div
            className="max-w-3xl mx-auto mb-6 px-5 py-4 bg-amber-50 border border-amber-200 rounded-xl"
            data-testid="booking-pending-banner"
          >
            <p className="text-sm text-amber-900">
              <strong>Pending airline confirmation:</strong>{' '}
              Your booking request has been submitted successfully, but the airline PNR/ticket is not available yet.
              Please check your Bookings page later — we’ll update it once confirmed.
            </p>
          </div>
        )}

        {/* Hold-specific banner — Sprint May-2026 */}
        {isHold && (
          <div
            className="max-w-3xl mx-auto mb-6 px-5 py-4 bg-amber-50 border border-amber-200 rounded-xl"
            data-testid="booking-hold-banner"
          >
            <p className="text-sm text-amber-900">
              <strong>Pay before:</strong>{' '}
              {(holdUntil || holdTimeLimit)
                ? new Date(holdUntil || holdTimeLimit).toLocaleString('en-IN', {
                    dateStyle: 'medium', timeStyle: 'short',
                  })
                : 'the deadline shown on your booking (or confirm promptly if no deadline was returned)'}
              . Go to your <a className="underline font-semibold" href="/agent/bookings">Bookings page</a>{' '}
              and click <strong>Confirm Hold</strong> to ticket. Hold amount: <strong>₹{Number(holdAmount || 0).toLocaleString('en-IN')}</strong>.
            </p>
          </div>
        )}

        {/* Seat-not-reserved notice (errCode 1131 fallback) */}
        {seatWarning && (
          <div
            className="max-w-3xl mx-auto mb-6 px-5 py-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3"
            data-testid="booking-seat-warning"
          >
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 font-bold">!</div>
            <div className="text-sm text-amber-900">
              <p className="font-semibold mb-1">Seat preference not reserved</p>
              <p>{seatWarning}</p>
            </div>
          </div>
        )}

        {/* Booking Details Card */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-6">
          {/* Booking ID */}
          <div className="text-center mb-8 pb-6 border-b-2 border-dashed border-gray-200">
            <p className="text-sm text-gray-500 mb-2">Booking Reference</p>
            <p className="text-3xl font-bold text-[#1B375B]">{bookingRefShown}</p>
            {bookingRefShown === '—' && (
              <p className="text-sm text-gray-500 mt-2">Your booking reference is available on the Bookings page.</p>
            )}
          </div>

          {/* Payment Details (hidden in hold mode — no money has moved yet) */}
          {!isHold && (
          <div className="space-y-4">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Payment Summary</h3>

            <div className="flex items-center justify-between py-3 px-4 bg-gradient-to-r from-green-50 to-teal-50 rounded-lg border border-green-200">
              <div className="flex items-center gap-3">
                {paymentMode === 'CREDIT' && (
                  <>
                    <Building2 className="w-6 h-6 text-[#659A9B]" />
                    <div>
                      <p className="font-semibold text-gray-800">Paid via Credit Limit</p>
                      <p className="text-sm text-gray-600">Deducted from your credit account</p>
                    </div>
                  </>
                )}
                {paymentMode === 'WALLET' && (
                  <>
                    <Wallet className="w-6 h-6 text-[#659A9B]" />
                    <div>
                      <p className="font-semibold text-gray-800">Paid via Cash Wallet</p>
                      <p className="text-sm text-gray-600">Deducted from your wallet balance</p>
                    </div>
                  </>
                )}
                {paymentMode === 'GATEWAY' && (
                  <>
                    <CheckCircle className="w-6 h-6 text-green-600" />
                    <div>
                      <p className="font-semibold text-gray-800">Paid via Payment Gateway</p>
                      <p className="text-sm text-gray-600">Payment successful</p>
                    </div>
                  </>
                )}
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-gray-800">₹{amountDebited?.toLocaleString()}</p>
              </div>
            </div>

            {/* Remaining Balance */}
            {(paymentMode === 'CREDIT' || paymentMode === 'WALLET') && (
              <div className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-lg">
                <p className="text-gray-700 font-medium">
                  Remaining {paymentMode === 'CREDIT' ? 'Credit' : 'Wallet'} Balance
                </p>
                <p className="text-xl font-bold text-[#659A9B]">₹{remainingBalance?.toLocaleString()}</p>
              </div>
            )}
          </div>
          )}

          {/* Important Notice */}
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>📧 Confirmation Email:</strong> Your booking confirmation and e-ticket will be sent to your registered email shortly.
            </p>
          </div>

          {/* Next Steps */}
          <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm text-amber-800">
              <strong>⚠️ Important:</strong> Ticket issuance is in progress. You will receive your PNR and ticket details within 15-30 minutes. Please check your email or dashboard.
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => navigate('/agent/dashboard')}
            className="flex items-center justify-center gap-2 px-6 py-3 bg-[#659A9B] text-white rounded-lg font-semibold hover:bg-[#335E77] transition-colors"
          >
            <Home className="w-5 h-5" />
            Go to Dashboard
          </button>

          <button
            onClick={() => navigate('/agent/bookings')}
            className="flex items-center justify-center gap-2 px-6 py-3 bg-white border-2 border-[#659A9B] text-[#659A9B] rounded-lg font-semibold hover:bg-gray-50 transition-colors"
          >
            <Download className="w-5 h-5" />
            View Booking
          </button>

          <button
            onClick={() => navigate('/')}
            className="flex items-center justify-center gap-2 px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
          >
            <ArrowRight className="w-5 h-5" />
            Book Another Flight
          </button>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default BookingSuccess;
